/* eslint-disable react/no-unescaped-entities */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, ShieldCheck, Star, MapPin, Phone, Mail, ExternalLink,
} from "lucide-react";
import TestimonialsWall from "@/components/TestimonialsWall";
import TrustBadges from "@/components/TrustBadges";
import { RentalAnimation } from "@/components/landing/RentalAnimation";

/**
 * Dedicated paid-traffic landing page for Facebook Ads + Google Ads.
 * Designed for one job: get the visitor to click "Choose a plan" or
 * call/email the company. NO top-nav distractions. Includes the company
 * website URL prominently for ad-platform trust signals.
 */
export default function GetStarted() {
  useEffect(() => {
    document.title = "Get Central Rentals — Canadian Property Management Software";
    const setTag = (sel, attrs) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement(sel.startsWith("link") ? "link" : "meta"); document.head.appendChild(el); }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };
    const desc = "Central Rentals — Canadian property management software. Used by landlords across all 13 provinces. Start a paid plans from CA$35/month, cancel any time.";
    const url = "https://centralrentals.ca/get-started";
    const tags = [
      setTag('meta[name="description"]', { name: "description", content: desc }),
      setTag('link[rel="canonical"]', { rel: "canonical", href: url }),
      setTag('meta[property="og:title"]', { property: "og:title", content: "Property management software for Canadian landlords" }),
      setTag('meta[property="og:description"]', { property: "og:description", content: desc }),
      setTag('meta[property="og:url"]', { property: "og:url", content: url }),
      setTag('meta[property="og:image"]', { property: "og:image", content: "https://centralrentals.ca/og-image.png" }),
      setTag('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" }),
      // Ad-platform-friendly: tell crawlers "follow but don't index this
      // ads landing copy" so Google doesn't dedupe it against the homepage.
      setTag('meta[name="robots"]', { name: "robots", content: "noindex, follow, max-image-preview:large" }),
    ];
    // LocalBusiness JSON-LD on the landing page itself for Google Ads
    // location-extension matching and Facebook Ads policy review.
    const ld = {
      "@context": "https://schema.org",
      "@type": ["LocalBusiness", "ProfessionalService"],
      "name": "Central Rentals Canada",
      "image": "https://centralrentals.ca/og-image.png",
      "url": url,
      "telephone": "+1-780-000-0000",
      "email": "contact@centralrentals.ca",
      "priceRange": "$$",
      "areaServed": [
        "Alberta", "British Columbia", "Saskatchewan", "Manitoba", "Ontario",
        "Quebec", "New Brunswick", "Nova Scotia", "Prince Edward Island",
        "Newfoundland and Labrador", "Yukon", "Northwest Territories", "Nunavut"
      ].map((p) => ({ "@type": "AdministrativeArea", "name": p })),
    };
    let script = document.head.querySelector('script[data-landing-jsonld="1"]');
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-landing-jsonld", "1");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    return () => { tags.forEach((el) => el && el.remove()); if (script) script.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="get-started-page">
      {/* Lean header — logo only, plus the website URL spelled out so ad
          platforms can verify the destination matches the displayed domain. */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-home-link">
            <div>
              <div className="font-display text-base font-extrabold text-slate-900 leading-none">Central Rentals</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-bold mt-0.5">centralrentals.ca</div>
            </div>
          </Link>
          <a href="https://centralrentals.ca" target="_blank" rel="noreferrer"
            data-testid="landing-website-link"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-emerald-700 font-semibold">
            Visit website <ExternalLink className="w-3 h-3"/>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-12 pb-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] uppercase tracking-[0.18em] font-bold">
            <ShieldCheck className="w-3.5 h-3.5"/> Verified Canadian business
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[1.02] text-slate-900 mt-5">
            The property management <span className="text-emerald-700">software</span> Canadian landlords run on.
          </h1>
          <p className="mt-5 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
            Rent collection, compliant leases, tenant screening, maintenance, and T776 tax exports — all in one place. Built for landlords across <b>all 13 provinces and territories</b>.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to="/signup?role=landlord&plan=professional&utm_source=ads&utm_medium=landing&utm_campaign=get_started"
              data-testid="landing-cta-primary"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg shadow-emerald-200">
              Choose a plan <ArrowRight className="w-4 h-4"/>
            </Link>
            <a href="https://centralrentals.ca" target="_blank" rel="noreferrer"
              data-testid="landing-cta-secondary"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-slate-200 hover:border-emerald-700 text-slate-800 font-semibold">
              Browse the full site
            </a>
          </div>

          <ul className="mt-7 space-y-2.5 text-sm text-slate-700">
            {[
              "Cancel any time",
              "Rent tracking in CAD (peer-to-peer) with auto-receipts",
              "Ontario Standard Lease, BC RTB, Alberta RTDRS, Quebec Form F — all built in",
              "T776, NR4 and T5013 exports ready for your accountant",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-none"/>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div className="relative min-w-0">
            <RentalAnimation/>
          </div>
        </div>
      </section>

      {/* Province badges — visible "we serve everywhere" trust signal for Google Ads location-extension match */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-4 text-center">Used by landlords in all 13 provinces and territories</div>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              ["AB", "Alberta"], ["BC", "British Columbia"], ["SK", "Saskatchewan"],
              ["MB", "Manitoba"], ["ON", "Ontario"], ["QC", "Quebec"],
              ["NB", "New Brunswick"], ["NS", "Nova Scotia"], ["PE", "PEI"],
              ["NL", "Newfoundland"], ["YT", "Yukon"], ["NT", "NWT"], ["NU", "Nunavut"],
            ].map(([code, name]) => (
              <Link key={code} to={`/landlord-software/${name.toLowerCase().replace(/\s+/g, "-").replace("pei", "prince-edward-island").replace("nwt", "northwest-territories").replace("newfoundland", "newfoundland-and-labrador")}`}
                data-testid={`landing-prov-${code.toLowerCase()}`}
                className="px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:border-emerald-500 hover:text-emerald-700 transition-colors">
                <span className="text-emerald-600 font-bold">{code}</span> · {name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Social proof */}
      <TestimonialsWall limit={6} />

      <TrustBadges compact />

      <section className="max-w-5xl mx-auto px-5 md:px-8 py-14">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { stat: "3,800+", lab: "Google profile views in last 6 months" },
            { stat: "4.8 / 5", lab: "Average customer satisfaction" },
            { stat: "$30M+", lab: "Annualized rent collected on the platform" },
          ].map((s, i) => (
            <div key={i} className="p-6 rounded-2xl bg-white border border-slate-200">
              <div className="font-display text-3xl font-extrabold tracking-tighter text-slate-900">{s.stat}</div>
              <div className="text-sm text-slate-600 mt-1">{s.lab}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 p-6 rounded-2xl bg-slate-50 border border-slate-200">
          <div className="flex items-center gap-1 text-amber-500 mb-3">
            {[1,2,3,4,5].map((i) => <Star key={i} className="w-4 h-4 fill-current"/>)}
          </div>
          <blockquote className="text-lg italic text-slate-800 leading-relaxed">
            "We switched off three different spreadsheets and one Canadian-specific app onto Central Rentals last quarter. Rent reconciliation went from 6 hours per month to 20 minutes. Our T776 export saved our accountant a full afternoon."
          </blockquote>
          <div className="text-sm text-slate-500 mt-3 font-semibold">— Landlord, 38 doors across Alberta and Saskatchewan</div>
        </div>
      </section>

      {/* CTA + contact block (NAP info Google Maps + ad platforms verify against) */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900">
            Ready to switch off the spreadsheets?
          </h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">Paid plans from CA$35/month, cancel any time. Built and supported in Canada.</p>
          <Link to="/signup?role=landlord&plan=professional&utm_source=ads&utm_medium=landing&utm_campaign=get_started_footer"
            data-testid="landing-cta-footer"
            className="mt-7 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg shadow-emerald-200">
            Choose a plan <ArrowRight className="w-4 h-4"/>
          </Link>

          {/* NAP block — email + phone only; no street address */}
          <div className="mt-12 pt-8 border-t border-slate-200 grid sm:grid-cols-2 gap-6 text-left text-sm max-w-2xl mx-auto">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-emerald-700 mt-0.5 flex-none"/>
              <div>
                <div className="font-bold text-slate-900">Email</div>
                <a href="mailto:contact@centralrentals.ca" className="text-emerald-700 hover:underline" data-testid="landing-email">contact@centralrentals.ca</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <ExternalLink className="w-5 h-5 text-emerald-700 mt-0.5 flex-none"/>
              <div>
                <div className="font-bold text-slate-900">Website</div>
                <a href="https://centralrentals.ca" target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline" data-testid="landing-website-footer">centralrentals.ca</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-6 text-center text-xs text-slate-500">
          <Link to="/privacy" className="hover:text-slate-700 mr-4">Privacy</Link>
          <Link to="/terms" className="hover:text-slate-700">Terms</Link>
          <div className="mt-2">© Central Rentals Canada Inc. · centralrentals.ca</div>
        </div>
      </footer>
    </div>
  );
}
